import Immutable from "immutable";
import { jwtDecode } from "jwt-decode";
import { Amplify } from "aws-amplify";
import { signIn, signOut, getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import {
    CognitoIdentityClient,
    GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import fileSaver from "file-saver";
import { delay } from "redux-saga";
import {
    CallEffect,
    PutEffect,
    SelectEffect,
    TakeEffect,
    all,
    call,
    put,
    race,
    select,
    take,
    takeLatest,
} from "redux-saga/effects";

import {
    AppHelpers,
    LocalStorageHelpers,
    APIErrorWithCode,
    AuthenticationAPI,
    LayerAPI,
    SystemAPI,
    SearchAPI,
    isSkyUrl,
    urlBase,
    Credentials,
    SkyAPI,
} from "@ai360/core";

import { getCrossSubdomainRedirect, isSsoRedirectSuppressed, redirectTo } from "./sso-redirect";
import { isSkyIdpForced } from "./sky-idp";
import { getLastUsedUser } from "./last-used-user";

import { actions as cdActions } from "~/customer-data";
import { errorCodeMessages } from "~/i18n-error-messages";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import * as messagingActions from "~/messaging/actions";
import { setIsLoading } from "~/theme";
import * as actions from "./actions";
import { messages } from "./i18n-messages";
import * as selectors from "./selectors";
import { IActionData } from "./interfaces";
import { PayloadAction } from "typesafe-actions/dist/types";
import { PersonalityTypes } from "~/utils/keywords";

const CUSTOMER_ID = "2";
const SALESPERSON_ID = "4";

const { LoginState } = actions;

const getLoginBackgroundImage = function* () {
    try {
        const response = yield call(AuthenticationAPI.getLoginBackgroundUrl);
        yield put(actions.setLoginBackgroundUrl(response));
    } catch (error) {
        console.error(error);
    }
};

const getSystemFaviconUrl = function* () {
    try {
        const UserGuid = yield select(selectors.getTheUserGuid);
        const companyGuid = yield select(selectors.getTheUserCompanyGuid);
        const response = yield call(SystemAPI.getSystemFaviconUrl, {
            UserGuid,
            Model: companyGuid,
        });
        yield put(actions.setSystemFaviconUrl(response));
    } catch (error) {
        console.error(error);
    }
};

export const getMobileAppDetails = function* (): Generator<
    CallEffect | PutEffect<IActionData> | SelectEffect,
    void,
    any
> {
    try {
        const UserGuid = yield select(selectors.getTheUserGuid);
        const response = yield call(SystemAPI.getMobileAppDetails, {
            UserGuid,
        });
        yield put(actions.setMobileAppDetails(response));
    } catch (error) {
        console.error(error);
    }
};

export const onFetchEnrollmentForm = function* (
    action: IActionData
): Generator<Promise<any>, void, any> {
    const { customerGuid } = action;
    try {
        const response = yield AuthenticationAPI.getEnrollmentForm(customerGuid);
        const fileType = response.url.split(".").pop();
        fileSaver.saveAs(AppHelpers.base64ToBlob(response.file), "EnrollmentAgreement." + fileType);
    } catch (error) {
        console.error(error);
    }
};

export const onFetchFilteredCustomerList = function* (
    action: IActionData
): Generator<CallEffect | PutEffect<PayloadAction<string, boolean> | IActionData>, void, any> {
    const { orgLevelGuid, userGuid } = action;
    yield put(setIsLoading(true));
    try {
        yield put(actions.setFilteredCustomers([], null));
        const allCustomersCall = call(SearchAPI.getCustomers, {
            userGuid,
            locationOrgLevelGuid: orgLevelGuid,
            active: true,
            userAccess: SearchAPI.UserAccess.UserHierarchyAccessUnfiltered,
        });

        const allCustomers: SearchAPI.ILegacyCustomerResult[] = yield allCustomersCall;

        const lastUsedCustomerCall = call(SearchAPI.getCustomers, {
            userGuid,
            locationOrgLevelGuid: orgLevelGuid,
            active: true,
            lastUsedCustomer: SearchAPI.LastUsedCustomer.Remove,
        });

        const lastUsedCustomer: SearchAPI.ILegacyCustomerResult[] = yield lastUsedCustomerCall;

        const lastUsedCustomerGuid = Immutable.Set(
            lastUsedCustomer.map((x) => x.customerGuid)
        ).first();

        yield put(actions.setFilteredCustomers(allCustomers, lastUsedCustomerGuid));
    } catch (error) {
        console.error(error);
    } finally {
        yield put(setIsLoading(false));
    }
};

export const onInitialMobileLoad = function* (): Generator<CallEffect, void, unknown> {
    yield call(getMobileAppDetails);
};

export const onInitialEnrollmentLoad = function* (): Generator<CallEffect, void, unknown> {
    yield call(getLoginBackgroundImage);
    yield call(getSystemFaviconUrl);
    document.title = "Enrollment";
};

export const onInitialMyAgDataLoad = function* (
    action: IActionData
): Generator<CallEffect, void, unknown> {
    document.title = action.pageTitle ?? "MyAgData Registration";
    yield call(getLoginBackgroundImage);
    yield call(getSystemFaviconUrl);
};

export const messageSubscriptions = function* () {
    yield put(
        messagingActions.subscribe(0, {
            eventName: "orgLevelChanged",
            action: (message) => {
                return actions.orgLevelChanged(
                    message.companyGuid,
                    message.customerGuid,
                    message.locationGuid,
                    message.userGuid
                );
            },
        })
    );
};

let orgLevelChangeHandled = false;

export const orgLevelChanged = function* (action: IActionData) {
    const { companyGuid, customerGuid, locationGuid, userGuid } = action.payload;
    const { lastUsedCompanyGuid, lastUsedCustomerGuid, lastUsedLocationGuid } = yield select(
        selectors.getUser
    );
    if (
        orgLevelChangeHandled ||
        (lastUsedCompanyGuid === companyGuid &&
            lastUsedCustomerGuid === customerGuid &&
            lastUsedLocationGuid === locationGuid)
    ) {
        return;
    }
    yield put(notificationActions.pushToasterMessage(messages.orgLevelChanged, MSGTYPE.WARNING));
    LocalStorageHelpers.set(LocalStorageHelpers.USER_GUID, userGuid);
    LocalStorageHelpers.set(LocalStorageHelpers.COMPANY_GUID, companyGuid);

    orgLevelChangeHandled = true;

    setTimeout(() => {
        window.location.reload();
    }, 5000);
};

const setSessionFromKey = function* (key, history) {
    const { email, session } = yield call(AuthenticationAPI.keyLogin, key);

    const cognitoConfig = JSON.parse(LocalStorageHelpers.get(LocalStorageHelpers.COGNITO_CONFIG));
    const clientId = cognitoConfig.userPoolClientId;
    const username = email.toLowerCase();

    // Write tokens directly to the localStorage keys Amplify reads
    LocalStorageHelpers.set(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
    LocalStorageHelpers.set(
        `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`,
        session.idToken
    );
    LocalStorageHelpers.set(
        `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`,
        session.accessToken
    );
    LocalStorageHelpers.set(
        `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`,
        session.refreshToken
    );

    yield call(fetchLoginUser, history, false, true);
};

const FETCH_LOGIN_USER_RETRIES = 3;
const FETCH_LOGIN_USER_RETRY_DELAY_MS = 1000;

export const checkSessionIsActive = function* (history) {
    const cognitoConfigJson = LocalStorageHelpers.get(LocalStorageHelpers.COGNITO_CONFIG);
    if (cognitoConfigJson != null) {
        // Validate the Cognito session separately from bootstrapping the app.
        // A failure to load app data must NOT be mistaken for an inactive
        // session — otherwise an already-authenticated user is dropped onto a
        // login form they can't use, where signIn throws
        // UserAlreadyAuthenticatedException ("There is already a signed in user.").
        let hasActiveSession = false;
        try {
            yield call(getCurrentUser);
            yield call(fetchAuthSession);
            // Validate the Cognito session ONLY here — do not bootstrap the app in this
            // try. fetchLoginUser is the full bootstrap (it dispatches setUserInfo →
            // resetApp and navigates home); calling it here as well as in the retry loop
            // below ran it twice (double resetApp / redundant load). Bootstrapping happens
            // once, in the retry loop.
            hasActiveSession = true;
        } catch (err) {
            console.warn("No active Cognito session", err);
        }

        if (hasActiveSession) {
            // Already authenticated — recover the user into the app rather than
            // showing the login form. Retry transient bootstrap failures first.
            for (let attempt = 1; attempt <= FETCH_LOGIN_USER_RETRIES; attempt++) {
                try {
                    // Session-restore bootstrap (pass true so the SSO cross-subdomain
                    // redirect / stale-last-used check runs — see resolveSsoSessionRestore).
                    yield call(fetchLoginUser, history, true);
                    return;
                } catch (err) {
                    console.warn(
                        `Login bootstrap failed (attempt ${attempt}/${FETCH_LOGIN_USER_RETRIES})`,
                        err
                    );
                    if (attempt < FETCH_LOGIN_USER_RETRIES) {
                        yield call(delay, FETCH_LOGIN_USER_RETRY_DELAY_MS);
                    }
                }
            }
            // Session is valid but the app cannot be loaded. Clear the session
            // so the login form below is actually usable as a fallback.
            console.error("Login bootstrap failed after retries; signing out");
            try {
                yield call(signOut);
            } catch (err) {
                console.warn("signOut during session recovery failed", err);
            }
        }
    }
    if (isSkyUrl) {
        window.location.href = window.location.origin;
    }
    yield put(actions.setLoginState(LoginState.LOGIN_FORM));
    yield put(actions.setProcessing(false));
};

export const fetchCognitoConfig = function* () {
    const existingConfigJson = LocalStorageHelpers.get(LocalStorageHelpers.COGNITO_CONFIG);
    const idpConfig = yield call(AuthenticationAPI.getIdpConfig);

    const useSkyIdp = isSkyIdpForced();
    const cognitoConfig = {
        userPoolId: useSkyIdp ? idpConfig.skyCognitoUserPoolId : idpConfig.cognitoUserPoolId,
        identityPoolId: idpConfig.iotIdentityPoolId,
        userPoolClientId: useSkyIdp
            ? idpConfig.skyIdentityProviderClientId
            : idpConfig.identityProviderClientId,
        region: useSkyIdp ? idpConfig.skyRegion : idpConfig.region,
    };

    if (existingConfigJson != null) {
        const existingConfig = JSON.parse(existingConfigJson);
        if (existingConfig.userPoolId !== cognitoConfig.userPoolId) {
            LocalStorageHelpers.remove(LocalStorageHelpers.COGNITO_CONFIG);
        }
    }

    const skyUserInfo = JSON.parse(LocalStorageHelpers.get("USER_INFO_KEY"));
    if (skyUserInfo != null) {
        const lastAuthUser = skyUserInfo.email;
        const accessToken = LocalStorageHelpers.get("cognito-access-token");
        let idToken = LocalStorageHelpers.get("cognito-id-token");
        const refreshToken = LocalStorageHelpers.get("cognito-refresh-token");

        if (idToken == null) {
            // Configure Amplify temporarily to refresh tokens
            Amplify.configure({
                Auth: {
                    Cognito: {
                        userPoolId: cognitoConfig.userPoolId,
                        userPoolClientId: cognitoConfig.userPoolClientId,
                    },
                },
            });
            try {
                const session = yield call(fetchAuthSession, { forceRefresh: true });
                idToken = session.tokens?.idToken?.toString();
            } catch (err) {
                console.warn("Failed to refresh SKY session", err);
            }
        }
        if (idToken) {
            const jwt: any = jwtDecode(idToken);
            const skyClientId = jwt.aud;

            LocalStorageHelpers.set(
                `CognitoIdentityServiceProvider.${skyClientId}.LastAuthUser`,
                lastAuthUser
            );
            LocalStorageHelpers.set(
                `CognitoIdentityServiceProvider.${skyClientId}.${lastAuthUser}.accessToken`,
                accessToken
            );
            LocalStorageHelpers.set(
                `CognitoIdentityServiceProvider.${skyClientId}.${lastAuthUser}.idToken`,
                idToken
            );
            LocalStorageHelpers.set(
                `CognitoIdentityServiceProvider.${skyClientId}.${lastAuthUser}.refreshToken`,
                refreshToken
            );

            cognitoConfig.userPoolClientId = skyClientId;
            cognitoConfig.userPoolId = idpConfig.skyCognitoUserPoolId;
        }
    }
    LocalStorageHelpers.set(LocalStorageHelpers.COGNITO_CONFIG, JSON.stringify(cognitoConfig));

    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId: cognitoConfig.userPoolId,
                userPoolClientId: cognitoConfig.userPoolClientId,
            },
        },
    });
};

/**
 * A "connected type" is an Agvance/Helena-corporate personality that requires an
 * SSO connection to reach. DISCONNECTED (and anything else) is not connected-type
 * and is always reachable, so it never needs an SSO connection.
 */
const isConnectedType = (pid) =>
    pid === PersonalityTypes.AGVANCE_STANDARD || pid === PersonalityTypes.HELENA_CORPORATE;

/**
 * Reconcile the server's last-used company (orgLevelGuid from GetLoginUser)
 * against the login's SSO connections. Returns one of:
 *
 *   'redirect'        — the last-used org is connected on a DIFFERENT host; a
 *                       cross-subdomain redirect was issued (caller must return).
 *   'clear-last-used' — the last-used org is a connected-type personality but has
 *                       no matching connection anymore; the stale last-used should
 *                       be cleared so the user lands on company selection.
 *   'select-company'  — the login has MIXED access (both connected and disconnected
 *                       orgs) AND its last-used org is connected on a DIFFERENT
 *                       host: never auto-load or auto-redirect — the user must pick
 *                       on the company-selection screen, which redirects to SSO if
 *                       they choose a connected org. Applies to fresh logins AND
 *                       session-restore/reload (it also suppresses the 'redirect'
 *                       outcome so the user actually gets to choose). A last-used
 *                       reachable at the CURRENT url — connected on this host or a
 *                       disconnected org — goes straight in instead ('none'), even
 *                       with mixed access. Only key logins (allowForceSelect=false)
 *                       are exempt: they are machine handoffs (SKY/SSO handing the
 *                       user into a specific org), where a selection prompt would
 *                       break the handoff — including re-prompting a user who JUST
 *                       picked their connected company on the selection screen.
 *   'none'            — load normally (SSO not enabled, last-used reachable at the
 *                       current url, or any SSO lookup failure).
 *
 * The host check is mandatory to prevent a reload loop on the connected subdomain
 * (this runs on every load).
 */
export const resolveSsoSessionRestore = function* (
    lastUsedCompanyGuid,
    email,
    lastUsedOwnerPersonalityId,
    allowRedirect = false,
    allowForceSelect = false
) {
    if (!lastUsedCompanyGuid || !email) {
        return "none";
    }
    let statusResult;
    let connectionsResult;
    try {
        // Only query connections when SSO is actually enabled for this login.
        statusResult = yield put(SkyAPI.default.endpoints.getSsoStatus.initiate(email));
        const status = yield call([statusResult, statusResult.unwrap]);
        if (!status || !status.ssoEnabled) {
            return "none";
        }
        // Mixed access forces company selection ONLY when the last-used org is connected
        // on a different host (fresh login and session-restore alike; key logins pass
        // allowForceSelect=false). A last-used reachable at the current url goes straight
        // in. A stale connected-type last-used must still return 'clear-last-used' so the
        // stale guid is actually cleared, not just skipped.
        const forceSelect = allowForceSelect && status.status === SkyAPI.SsoConnectionStatus.Mixed;
        connectionsResult = yield put(SkyAPI.default.endpoints.getSsoConnections.initiate());
        const connections = yield call([connectionsResult, connectionsResult.unwrap]);
        const connection =
            (connections || []).find(
                (c) => c.orgLevelGuid === lastUsedCompanyGuid && c.isConnected
            ) || null;
        if (connection) {
            const redirectUrl = getCrossSubdomainRedirect(connection, window.location.host);
            if (redirectUrl) {
                // Connected on a DIFFERENT host. Mixed access suppresses the redirect and
                // forces selection — bouncing the user to the connected org's site would
                // deny them the company choice the selection screen exists to give. The
                // redirect itself only applies on session-restore/reload (allowRedirect);
                // on a fresh login the email step already handled it.
                if (forceSelect) {
                    return "select-company";
                }
                if (allowRedirect) {
                    if (isSsoRedirectSuppressed()) {
                        console.warn("SSO redirect suppressed (local dev):", redirectUrl);
                        return "none";
                    }
                    yield call(redirectTo, redirectUrl);
                    return "redirect";
                }
                return "none";
            }
            // Connected at the CURRENT url — straight in, even with mixed access.
            return "none";
        }
        // No connected connection for the last-used company. If it is a connected-type
        // personality, the last-used is stale and must be cleared; otherwise (a
        // disconnected last-used) it is reachable at the current url, so straight in,
        // even with mixed access.
        if (isConnectedType(lastUsedOwnerPersonalityId)) {
            return "clear-last-used";
        }
        return "none";
    } catch (err) {
        // SSO lookup failed — fall through to normal session restore
        return "none";
    } finally {
        if (statusResult && statusResult.unsubscribe) {
            statusResult.unsubscribe();
        }
        if (connectionsResult && connectionsResult.unsubscribe) {
            connectionsResult.unsubscribe();
        }
    }
};

export const fetchLoginUser = function* (
    history,
    isSessionRestore = false,
    isKeyLogin = false
): Generator<CallEffect | PutEffect<IActionData> | TakeEffect | SelectEffect, void, any> {
    const response = yield call(AuthenticationAPI.getLoginUser);
    if (response.loginGuid != null) {
        yield put(actions.setLoginGuid(response.loginGuid));
    }
    // Make the login email available in redux (getLoginEmail) for both fresh logins and
    // session-restore/reload, so components (e.g. select-company) can query getSsoStatus.
    if (response.email) {
        yield put(actions.setLoginEmail(response.email));
    }

    if (response.isFirstTimeLogin || !response.isEulaAcceptanceUpToDate) {
        yield put(
            actions.setEulaInfo(response.eula, response.loginGuid, response.isFirstTimeLogin)
        );
        yield put(actions.setUsersInfo(response.theUsers));
        yield put(actions.setSecurityInfo(response.email));
        yield put(actions.setLoginState(LoginState.EULA));
        yield put(actions.setReleaseNotes(response.releaseNotes));
        yield put(actions.setProcessing(false));
        return;
    }

    // Resolve the SSO status of the last-used company on EVERY login path (fresh login,
    // session-restore, and key login). A connected-type last-used with no live connection
    // is stale and must be cleared so we never auto-load an unreachable org. The
    // cross-subdomain redirect is only performed on session-restore (allowRedirect), since
    // a fresh login already handled connected-elsewhere redirects at the email step. MIXED
    // access with a connected-elsewhere last-used instead forces the company-selection
    // screen on fresh login AND session-restore; a last-used reachable at the current url
    // goes straight in, and key logins (machine handoffs into a specific org) are exempt.
    let clearedLastUsed = false;
    const lastUsedCompanyGuid =
        response.theUsers && response.theUsers.length > 0
            ? response.theUsers[0].lastUsedCompanyGuid
            : null;
    const lastUsedUser0 =
        response.theUsers && response.theUsers.find((u) => u.companyGuid === lastUsedCompanyGuid);
    const outcome = yield call(
        resolveSsoSessionRestore,
        lastUsedCompanyGuid,
        response.email,
        lastUsedUser0 && lastUsedUser0.ownerPersonalityId,
        isSessionRestore,
        !isKeyLogin
    );
    if (outcome === "redirect") {
        return;
    }
    // Mixed connected/disconnected access: skip every auto-select path below (the
    // last-used stays intact as the selection screen's preselect) and land on
    // SELECT_COMPANY so the user chooses; picking a connected org redirects to its
    // SSO site from there.
    const forceSelection = outcome === "select-company";
    if (outcome === "clear-last-used") {
        // Persist the cleared last-used to the server and drop the cached company so
        // company selection starts fresh instead of auto-picking the stale org.
        yield call(
            AuthenticationAPI.SetLastUsedLogin,
            response.theUsers[0].userGuid,
            null,
            null,
            null
        );
        LocalStorageHelpers.remove(LocalStorageHelpers.COMPANY_GUID);
        response.theUsers = response.theUsers.map((u) => ({
            ...u,
            lastUsedCompanyGuid: null,
            lastUsedLocationGuid: null,
            lastUsedCustomerGuid: null,
        }));
        clearedLastUsed = true;
    }

    const legacyDb = LocalStorageHelpers.get("LEGACY_DB"); // This will be set when coming from SKY

    if (clearedLastUsed || forceSelection) {
        // Stale connected-type last-used was cleared, or mixed access forces company
        // selection — do NOT auto-select. Fall through to the SELECT_COMPANY tail
        // below so the user lands on company selection.
    } else if (response.theUser == null) {
        const lastUsedUser = getLastUsedUser(response.theUsers);

        if (lastUsedUser) {
            response.theUser = lastUsedUser;
            LocalStorageHelpers.set(LocalStorageHelpers.COMPANY_GUID, response.theUser.companyGuid);
            yield put(
                actions.setUserInfo(
                    response.theUser,
                    response.theUser.lastUsedCompanyGuid,
                    response.theUser.lastUsedLocationGuid,
                    response.theUser.lastUsedCustomerGuid
                )
            );
            yield take(actions.SET_USER_INFO_COMPLETE);
            history.replace(urlBase);
        } else if (
            legacyDb &&
            response.theUsers.some(
                (u) =>
                    u.ownerPersonalityId === PersonalityTypes.AGVANCE_STANDARD ||
                    u.ownerPersonalityId === PersonalityTypes.HELENA_CORPORATE
            )
        ) {
            const lastUsedCompanyGuid = response.theUsers[0].lastUsedCompanyGuid;

            const lastUsedUser = response.theUsers.find(
                (u) => u.companyGuid === lastUsedCompanyGuid
            );

            // The API only returns disconnected and the single Agvance DB the sky token auths
            response.theUser =
                lastUsedUser ||
                response.theUsers.find(
                    (u) =>
                        u.ownerPersonalityId === PersonalityTypes.AGVANCE_STANDARD ||
                        u.ownerPersonalityId === PersonalityTypes.HELENA_CORPORATE
                );
            console.assert(response.theUser != null);
            LocalStorageHelpers.set(LocalStorageHelpers.COMPANY_GUID, response.theUser.companyGuid);

            const newLastUsedCompanyGuid =
                lastUsedCompanyGuid || response.theUser.lastUsedCompanyGuid;
            const newLastUsedLocationGuid =
                lastUsedCompanyGuid === newLastUsedCompanyGuid
                    ? response.theUser.lastUsedLocationGuid
                    : null;
            const newLastUsedCustomerGuid =
                lastUsedCompanyGuid === newLastUsedCompanyGuid
                    ? response.theUser.lastUsedCustomerGuid
                    : null;

            yield put(
                actions.setUserInfo(
                    response.theUser,
                    newLastUsedCompanyGuid,
                    newLastUsedLocationGuid,
                    newLastUsedCustomerGuid
                )
            );
            yield take(actions.SET_USER_INFO_COMPLETE);
            history.replace(urlBase);
        }
    } else {
        console.assert(response.theUser != null);
        yield put(
            actions.setUserInfo(
                response.theUser,
                response.theUser.lastUsedCompanyGuid,
                response.theUser.lastUsedLocationGuid,
                response.theUser.lastUsedCustomerGuid
            )
        );
        history.replace(urlBase);
    }

    console.assert(response.theUsers != null);
    yield put(actions.setUsersInfo(response.theUsers));
    yield put(actions.setLoginState(LoginState.SELECT_COMPANY));
    const userGuid = yield select(selectors.getTheUserGuid);
    const imageryTileBucketName = yield call(LayerAPI.getImageryTileBucketName, userGuid);
    yield put(actions.setImageryTileBucketName(imageryTileBucketName));
    yield put(actions.setReleaseNotes(response.releaseNotes));
    yield put(actions.setProcessing(false));
};
const autoLogin = function* (action) {
    yield put(actions.setProcessing(true));
    yield call(getLoginBackgroundImage);
    yield call(getSystemFaviconUrl);
    const userGuid = yield select(selectors.getTheUserGuid);
    const imageryTileBucketName = yield call(LayerAPI.getImageryTileBucketName, userGuid);
    yield put(actions.setImageryTileBucketName(imageryTileBucketName));

    const { key, history } = action;
    try {
        yield call(fetchCognitoConfig);
        if (key != null) {
            yield call(setSessionFromKey, key, history);
            return;
        }
        yield call(checkSessionIsActive, history);
    } catch (error) {
        if (!(error instanceof APIErrorWithCode)) {
            console.error(error);
        }
        if (isSkyUrl) {
            window.location.href = window.location.origin;
        }
        yield put(actions.resetLogin());
        yield put(actions.setProcessing(false));
    }
};

const fetchSessionInfo = async (username: string, password: string) => {
    try {
        // Defense in depth: never call signIn while a session lingers, which
        // throws UserAlreadyAuthenticatedException ("There is already a signed
        // in user."). The user is submitting credentials, so clear any existing
        // session first.
        try {
            if (await getCurrentUser()) {
                await signOut();
            }
        } catch {
            /* no current user — nothing to clear */
        }
        await signIn({ username, password });
        await fetchAuthSession();
    } catch (err: any) {
        const name = err?.name || err?.__type || err?.code;
        if (name === "UserNotFoundException" || name === "NotAuthorizedException") {
            throw errorCodeMessages[1];
        }
        throw err;
    }
};

const login = function* (action) {
    const { email, history, password } = action;
    const cognitoConfigJson = LocalStorageHelpers.get(LocalStorageHelpers.COGNITO_CONFIG);
    if (cognitoConfigJson == null) {
        try {
            yield call(fetchCognitoConfig);
        } catch (error) {
            yield put(actions.setError(error));
            return;
        }
    }
    try {
        yield call(fetchSessionInfo, email.toLowerCase(), password);
        yield call(fetchLoginUser, history);
    } catch (error) {
        yield put(actions.setError(error));
    }
};

const logoutUser = async function () {
    try {
        await signOut();
    } catch (error) {
        console.error("Encountered an error while attempting to disconnect and logout: ", error);
    } finally {
        // Preserve the "Remember Me" email across logout so it can pre-fill the login
        // form next time — localStorage.clear() would otherwise wipe it.
        const rememberMe = LocalStorageHelpers.get(LocalStorageHelpers.REMEMBER_ME);
        const rememberedUsername = LocalStorageHelpers.get(LocalStorageHelpers.USERNAME);
        window.localStorage.clear();
        if (rememberMe) {
            LocalStorageHelpers.set(LocalStorageHelpers.REMEMBER_ME, rememberMe);
            if (rememberedUsername) {
                LocalStorageHelpers.set(LocalStorageHelpers.USERNAME, rememberedUsername);
            }
        }
        window.onbeforeunload = null;
        window.location.reload();
    }
};

const monitorActivity = function* () {
    const inactivityTimeoutSeconds = 7200;
    const timerIntervalSeconds = 30;

    let lastActivityIndicated = Math.floor(+new Date() / 1000);
    let lastDelayCallback = Math.floor(+new Date() / 1000);

    let userActivity = false;
    const indicateActivity = () => {
        userActivity = true;
    };
    window.addEventListener("mousemove", indicateActivity, false);
    window.addEventListener("keypress", indicateActivity, false);

    /**
     * 1. Log the user out automatically after `inactivityTimeoutMillis`
     * 2. Refresh the token if it will timeout before the next `timerIntervalMillis`
     *    Amplify auto-refreshes tokens when calling fetchAuthSession()
     */

    while (true) {
        const { logoutAction } = yield race({
            _: call(delay, timerIntervalSeconds * 1000),
            logoutAction: take(actions.LOGOUT_USER),
        });
        if (logoutAction != null) {
            // explicitly logout action posted
            break;
        }

        const now = Math.floor(+new Date() / 1000);
        let session;
        try {
            // fetchAuthSession auto-refreshes tokens only when expired;
            // we first check without forcing, then force-refresh if close to expiry
            session = yield call(fetchAuthSession);
        } catch (err) {
            console.warn("Error fetching session", err);
            // This generally only happens when a machine resumes from suspend ..
            // if we've missed more than 3 iterations on this loop, we just logout the
            // user, which should refresh the app.  We have to do this because signalR
            // will disconnect without any status change event, so the app won't get
            // the updates and there's no indication in the UI that anything's wrong.
            const callbackDelayTimeout = timerIntervalSeconds * 3;
            const timeSinceDelayCallback = now - lastDelayCallback;
            if (timeSinceDelayCallback > callbackDelayTimeout) {
                yield put(actions.logoutUser());
                break;
            }
            continue;
        }
        lastDelayCallback = now;

        // Get token expiration from the JWT payload
        let idTokenStr = session.tokens?.idToken?.toString();
        if (idTokenStr) {
            let decoded: any = jwtDecode(idTokenStr);
            let expiration = decoded.exp || 0;
            let sessionTimeoutSeconds = expiration - now;

            // Proactively force-refresh when token is within 5 minutes of expiry
            if (sessionTimeoutSeconds > 0 && sessionTimeoutSeconds < 300) {
                try {
                    session = yield call(fetchAuthSession, { forceRefresh: true });
                    idTokenStr = session.tokens?.idToken?.toString();
                    if (idTokenStr) {
                        decoded = jwtDecode(idTokenStr);
                        expiration = decoded.exp || 0;
                        sessionTimeoutSeconds = expiration - now;
                    }
                } catch (err) {
                    console.warn("Force refresh failed", err);
                }
            }

            if (sessionTimeoutSeconds < 0) {
                // Token expired and refresh failed — logout
                yield put(actions.logoutUser());
                break;
            }
            if (sessionTimeoutSeconds < timerIntervalSeconds * 3) {
                // Amplify attempted refresh above; if we're still close to expiry,
                // warn the user
                yield put(
                    notificationActions.pushToasterMessage(
                        messages.unableToKeepAlive,
                        MSGTYPE.WARNING,
                        {
                            seconds: sessionTimeoutSeconds,
                        }
                    )
                );
            }
        }

        if (userActivity) {
            lastActivityIndicated = Math.floor(+new Date() / 1000);
        } else {
            const timeSinceLastActivity = now - lastActivityIndicated;
            const inactivityTimeRemaining = inactivityTimeoutSeconds - timeSinceLastActivity;
            if (inactivityTimeRemaining <= 0) {
                yield put(actions.logoutUser());
                break;
            }
            if (
                inactivityTimeRemaining <= timerIntervalSeconds * 2 &&
                inactivityTimeRemaining > timerIntervalSeconds
            ) {
                yield put(
                    notificationActions.pushToasterMessage(
                        messages.loginTimeoutWarning,
                        MSGTYPE.WARNING,
                        { seconds: inactivityTimeRemaining },
                        (inactivityTimeRemaining - 1) * 1000
                    )
                );
            }
        }
        userActivity = false;
    }

    window.removeEventListener("mousemove", indicateActivity, false);
    window.removeEventListener("keypress", indicateActivity, false);
};

const onSetUserInfo = function* (action) {
    const { theUser, selectedCompanyGuid, selectedCustomerGuid, selectedLocationGuid, isReset } =
        action;
    try {
        yield put(actions.resetApp());
        const theUsers = yield select(selectors.getUsers);
        if (theUsers) {
            yield put(
                actions.setUsersInfo([
                    ...theUsers.map((usr) => ({
                        ...usr,
                        lastUsedCompanyGuid: selectedCompanyGuid,
                        lastUsedCustomerGuid: selectedCustomerGuid,
                        lastUsedLocationGuid: selectedLocationGuid,
                    })),
                ])
            );
        }
        LocalStorageHelpers.set(LocalStorageHelpers.USER_GUID, theUser.userGuid);
        yield call(
            AuthenticationAPI.SetLastUsedLogin,
            theUser.userGuid,
            selectedCompanyGuid,
            selectedLocationGuid,
            selectedCustomerGuid
        );
        yield put(cdActions.clearAllSelectedFields());
        if (!isReset) {
            yield setupMessaging();
            yield put(actions.setUserInfoComplete(theUser));
        } else {
            yield put(actions.continueReset());
        }
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        return;
    }
};

const onReloginUser = function* () {
    yield put(setIsLoading(true));
    yield take([actions.CONTINUE_RESET]);
    yield put(setIsLoading(false));
    window.location.reload();
};

const setupMessaging = function* () {
    const iotConfig = yield call(retrieveAndSetIotConfig);

    const identityPoolId = iotConfig.iotIdentityPoolId;
    const endpoint = iotConfig.endpoint;
    const region = iotConfig.region;

    const userGuid = yield select(selectors.getTheUserGuid);
    const userTypeId = yield select(selectors.getUserTypeId);
    const userHasRealTimeUpdates = yield select(selectors.getUserHasRealTimeFieldUpdates);

    const orgLevelGuids =
        userHasRealTimeUpdates && userTypeId !== CUSTOMER_ID && userTypeId !== SALESPERSON_ID
            ? yield select(selectors.getOrgLevelGuids)
            : null;

    const messagingCredentials = yield call(retrieveMessagingCredentials, identityPoolId);

    yield put(
        messagingActions.start(userGuid, orgLevelGuids, endpoint, region, messagingCredentials)
    );
};

const retrieveAndSetIotConfig = function* () {
    const iotConfig = yield call(AuthenticationAPI.getIotConfig);
    LocalStorageHelpers.set(LocalStorageHelpers.IOT_CONFIG, JSON.stringify(iotConfig));
    return iotConfig;
};

const retrieveMessagingCredentials = function* (identityPoolId) {
    const iotCredentials = yield call(identityPoolCredentials, identityPoolId);
    return {
        accessKeyId: iotCredentials.AccessKeyId,
        secretKey: iotCredentials.SecretKey,
        sessionToken: iotCredentials.SessionToken,
    };
};

const identityPoolCredentials = async (identityPoolId) => {
    const cognitoConfig = JSON.parse(LocalStorageHelpers.get(LocalStorageHelpers.COGNITO_CONFIG));
    const region = cognitoConfig.region;
    const userPoolId = cognitoConfig.userPoolId;

    const jwt = await Credentials.userPoolToken();
    const login = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
    const logins = { [login]: jwt };

    const response = await AuthenticationAPI.getIotIdentityId(identityPoolId, logins);

    const client = new CognitoIdentityClient({ region });
    const command = new GetCredentialsForIdentityCommand({
        IdentityId: response.identityId,
        Logins: logins,
    });
    const result = await client.send(command);
    return result.Credentials;
};

export const userSaga = function* () {
    yield all([
        messageSubscriptions(),
        takeLatest(actions.AUTO_LOGIN, autoLogin),
        takeLatest(actions.FETCH_ENROLLMENT_FORM, onFetchEnrollmentForm),
        takeLatest(actions.FETCH_FILTERED_CUSTOMER_LIST, onFetchFilteredCustomerList),
        takeLatest(actions.INITIAL_MOBILE_LOAD, onInitialMobileLoad),
        takeLatest(actions.INITIAL_ENROLLMENT_LOAD, onInitialEnrollmentLoad),
        takeLatest(actions.INITIAL_MYAGDATA_LOAD, onInitialMyAgDataLoad),
        takeLatest(actions.LOGIN, login),
        takeLatest(actions.ORG_LEVEL_CHANGED, orgLevelChanged),
        takeLatest(actions.RELOGIN_USER, onReloginUser),
        takeLatest(actions.SET_USER_INFO, onSetUserInfo),
        takeLatest(actions.SET_USER_INFO_COMPLETE, monitorActivity),
        takeLatest(actions.SET_USER_INFO_COMPLETE, getSystemFaviconUrl),
        takeLatest(actions.LOGOUT_USER, logoutUser),
    ]);
};
